import axios, { type AxiosError } from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import type { GroupPlugin } from "@/components/global/types";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import navigationItemReferences from "../../../../constants/navigation-item-references";
import pages from "../../../../constants/pages";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useCoAppNavigation from "../../../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../../../hooks/useDatagridSorting";
import useToastAlert from "../../../../hooks/useToastAlert";
import { selectOrganizationId } from "../../../../redux/userSlice";
import CoAppDatagridHeader from "../../../global/components/datagrid/CoAppDatagridHeader";
import useCoAppDataGridFilters from "../../../global/components/datagrid/useCoAppDataGridFilters";
import CoAppStandardDataGrid from "../../../global/components/datagrid/CoAppStandardDataGrid";
import SettingsDataGridSelectionRow from "../../SettingsDataGridSelectionRow";
import CoAppDestructiveConfirmationModal from "../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../global/components/modals/CoAppEditNameModal";
import type { Plugin } from "../../../global/types";
import GroupPluginsColumns from "./columns/GroupPluginsColumns";

/** Passed from `GroupWithErrorBoundary` when the route was opened without `navigate(..., { state })` (e.g. sidebar). */
interface GroupNavState {
	name: string;
}

interface GroupPluginsProps {
	state?: GroupNavState | null;
}

export default function GroupPlugins({ state: stateFromBoundary }: GroupPluginsProps = {}) {
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { updateCoAppNavigation } = useCoAppNavigation();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("groupPlugins");
	const navigate = useNavigate();
	const organizationId = useSelector(selectOrganizationId);
	const { groupId } = useParams<{ groupId: string }>();
	const { pathname, state: stateFromLocation } = useLocation() as {
		pathname: string;
		state: GroupNavState | undefined;
	};
	/* Router `location.state` only exists when navigation passes `{ state }`. Otherwise the parent boundary injects `state` after fetching the group. */
	const state = (stateFromBoundary ?? stateFromLocation) as GroupNavState;

	const [groupPlugins, setGroupPlugins] = useState<GroupPlugin[]>([]);
	const [editGroupIsToggled, setEditGroupIsToggled] = useState<boolean>(false);
	const [deleteGroupIsToggled, setDeleteGroupIsToggled] = useState<boolean>(false);
	const [groupNameInput, setGroupNameInput] = useState<string>("");
	const [groupNameError, setGroupNameError] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(true);

	const togglePlugin = (plugin: GroupPlugin) => {
		if (!groupId) return;
		axios.put(apiRoutes.updateGroupPlugin(groupId, plugin.id), { active: !plugin.activeForGroup, platformId: plugin.platformId }, { headers: authHeader })
			.then(() => {
				init();
			})
			.catch((error: AxiosError) => {
				console.log(error);
				handleToastAlert(messageLevels.ERROR, messages.GROUP_PLUGIN_TOGGLE_ERROR_MSG);
			});
	};
	
	const columns = useMemo(() => 
		GroupPluginsColumns({
			togglePlugin
		})
	, []);

	const handleToggleGroupEditDialog = () => {
		setEditGroupIsToggled(!editGroupIsToggled);
		setGroupNameError("");
		setGroupNameInput("");
	};

	const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setGroupNameInput(e.target.value);
		setGroupNameError("");
	};

	const handleGroupNameChangeSubmit = () => {
		if (!groupNameInput) return;

		if (groupNameInput === state.name) {
			handleToggleGroupEditDialog();
			return;
		}

		axios.put(apiRoutes.updateGroup(groupId as string), { name: groupNameInput }, { headers: authHeader })
			.then(() => {
				setEditGroupIsToggled(false);
				updateCoAppNavigation(false, navigationItemReferences.groupNavItemIndex, groupNameInput, pathname);
			})
			.catch((e: AxiosError) => {
				console.error(e);
				if (e.response?.data && typeof e.response.data === "object" && "message" in e.response.data && 
					typeof e.response.data.message === "string" && e.response.data.message.includes("already exists")) {
					setGroupNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Group"));
					return;
				}
				handleToggleGroupEditDialog();
				handleToastAlert(messageLevels.ERROR, messages.GROUP_NAME_UPDATE_ERROR_MSG);
			});
	};

	const handleToggleDeleteGroupDialog = () => {
		setDeleteGroupIsToggled(!deleteGroupIsToggled);
	};

	const confirmGroupDeletion = () => {
		axios.delete(apiRoutes.deleteGroup + "/" + groupId, {
			headers: authHeader
		}).then(() => {
			updateCoAppNavigation(true, navigationItemReferences.groupNavItemIndex);
			navigate(pages.groupManagement, { replace: true });
			handleToggleDeleteGroupDialog();
		}).catch((err: AxiosError) => {
			console.error(err);
			handleToastAlert(messageLevels.ERROR, messages.GROUP_DELETION_ERROR_MSG);
			handleToggleDeleteGroupDialog();
		});
	};

	const handleOrganizationalPluginRetrieval = async () => {
		const plugins: Plugin[] = [];
		await axios.get(apiRoutes.getPlatformsAndPlugins(organizationId as string | number), { headers: authHeader })
			.then((response) => {
				if (response.data.length === 0) return;

				const platforms = response.data;

				platforms.forEach((platform: any) => {
					if (!platform.plugins) {
						return;
					}
					platform.plugins.forEach((plugin: Plugin) => {
						if (!plugin.active) return;
						plugins.push({
							id: plugin.id,
							name: plugin.name,
							groups: (plugin.groups as any[]).map((group: any) => group.name),
							active: plugin.active,
							platformId: platform.id,
							platformName: platform.name,
						});
					});
				});
			})
			.catch((error: AxiosError) => {
				console.error(error);
			});
		return plugins;
	};

	const init = async () => {
		await axios.get(apiRoutes.getGroupPlugins(groupId as string), { headers: authHeader })
			.then(async (response) => {
				const orgPlugins = await handleOrganizationalPluginRetrieval();
				let plugins = orgPlugins.map(orgPlugin => {
					const groupPlugin = response.data.find((groupPlugin: any) => groupPlugin.pluginId === orgPlugin.id);
					return {
						...orgPlugin,
						activeForGroup: groupPlugin === undefined ? false : groupPlugin.active
					};
				});
				plugins = plugins.sort((a, b) => a.name.localeCompare(b.name));
				setGroupPlugins(plugins);
				setLoading(false);
			})
			.catch((error: AxiosError) => {
				console.error(error);
			});
	};

	useEffect(() => {
		init();
		handleInitializeSortModel();
	}, [state.name]);

	const {
		apiRef,
		gridFilterModel,
		onFilterModelChange,
		quickFilterSearchProps,
		visibleRowCount,
		filterControl,
		filterButtonEl,
	} = useCoAppDataGridFilters(groupPlugins, loading);

	return (
		<>
			<CoAppDatagridHeader
				title={`${state.name} Plugin Management`}
				subResourceType="Plugin"
				resourceType="Group"
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={filterControl}
				addIsPresent={false}
				editIsPresent={true}
				editOnClickHandler={handleToggleGroupEditDialog}
				deleteIsPresent={true}
				deleteOnClickHandler={handleToggleDeleteGroupDialog}
			/>
			<CoAppStandardDataGrid
				apiRef={apiRef}
				filterButtonEl={filterButtonEl}
				filterModel={gridFilterModel}
				onFilterModelChange={onFilterModelChange}
				visibleRowCount={visibleRowCount}
				columns={columns}
				rows={groupPlugins}
				pinnedColumns={[]}
				allowSelection={false}
				targetResource="plugins"
				parentResource="group"
				parentResourceName={state.name}
				bulkDestructiveText="Remove"
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				selectionFooter={
					<SettingsDataGridSelectionRow
						resourceNoun="plugin"
						visibleCount={visibleRowCount}
						selectedCount={0}
						loading={loading}
						onClearSelection={() => {}}
					/>
				}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteGroupIsToggled}
				dialogTitle={`Delete ${state.name} group?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG(state.name, "group")}
				confirmClickHandler={confirmGroupDeletion}
				cancelClickHandler={handleToggleDeleteGroupDialog}
				actionText="Delete"
			/>
			<CoAppEditNameModal
				dialogOpen={editGroupIsToggled}
				dialogTitle="Edit group name"
				changeHandler={handleGroupNameChange}
				placeholderText={state.name}
				confirmClickHandler={handleGroupNameChangeSubmit}
				cancelClickHandler={handleToggleGroupEditDialog}
				actionText="Save"
				editNameError={groupNameError}
				setEditNameError={setGroupNameError}
			/>
		</>
	);
}